import { type datadogLogs } from '@datadog/browser-logs';

type DatadogLogsRuntimeConfig = {
    readonly runEnvironment: 'development' | 'staging' | 'production';
    readonly sha: string;
    readonly datadogClientToken: string | null;
    readonly datadogSite: string | null;
};

type DatadogLogger = typeof datadogLogs.logger;

type ApiNetworkErrorLogContext = {
    readonly method: string;
    readonly pathTemplate: string;
    readonly attempt: number;
    readonly maxAttempts: number;
    readonly mode?: string;
    readonly reason?: string;
    readonly online: boolean | null;
    readonly visibilityState: string | null;
    readonly errorName: string | null;
};

const MAX_BUFFERED_API_NETWORK_ERROR_LOGS = 50;
const MAX_BUFFERED_EXTENSION_EVENT_LOGS = 50;

let initializationPromise: Promise<void> | null = null;
let logger: DatadogLogger | null = null;
let datadogLogsDisabled = false;
const bufferedApiNetworkErrorLogs: Array<ApiNetworkErrorLogContext> = [];
const bufferedExtensionEventLogs: Array<{
    readonly eventName: string;
    readonly properties: Record<string, unknown>;
}> = [];

export function initializeDatadogLogs(
    config: DatadogLogsRuntimeConfig,
): Promise<void> {
    const clientToken = config.datadogClientToken?.trim();
    if (!clientToken) {
        datadogLogsDisabled = true;
        bufferedApiNetworkErrorLogs.length = 0;
        bufferedExtensionEventLogs.length = 0;
        return Promise.resolve();
    }

    if (!initializationPromise) {
        initializationPromise = import('@datadog/browser-logs')
            .then(({ datadogLogs }) => {
                datadogLogs.init({
                    clientToken,
                    site: config.datadogSite?.trim() || 'datadoghq.com',
                    service: 'web',
                    env: config.runEnvironment,
                    version: config.sha,
                    // Sentry owns browser error capture. Datadog logs are only
                    // for the explicit low-cardinality counters we emit.
                    forwardErrorsToLogs: false,
                    sessionSampleRate: 100,
                });
                logger = datadogLogs.logger;
                for (const context of bufferedApiNetworkErrorLogs.splice(0)) {
                    emitApiNetworkErrorLog(context);
                }
                for (const entry of bufferedExtensionEventLogs.splice(0)) {
                    emitExtensionEventLog(entry.eventName, entry.properties);
                }
            })
            .catch((error: unknown) => {
                datadogLogsDisabled = true;
                bufferedApiNetworkErrorLogs.length = 0;
                bufferedExtensionEventLogs.length = 0;
                throw error;
            });
    }

    return initializationPromise;
}

export function logApiNetworkError(context: ApiNetworkErrorLogContext): void {
    if (logger) {
        emitApiNetworkErrorLog(context);
        return;
    }

    if (datadogLogsDisabled) {
        return;
    }

    if (
        bufferedApiNetworkErrorLogs.length >=
        MAX_BUFFERED_API_NETWORK_ERROR_LOGS
    ) {
        bufferedApiNetworkErrorLogs.shift();
    }
    bufferedApiNetworkErrorLogs.push(context);
}

function emitApiNetworkErrorLog(context: ApiNetworkErrorLogContext): void {
    logger?.info('api.network_error', {
        event_name: 'api.network_error',
        count: 1,
        method: context.method,
        path_template: context.pathTemplate,
        attempt: context.attempt,
        max_attempts: context.maxAttempts,
        mode: context.mode ?? null,
        reason: context.reason ?? null,
        online: context.online,
        visibility_state: context.visibilityState,
        error_name: context.errorName,
    });
}

export function logExtensionEvent(
    eventName: string,
    properties: Record<string, unknown>,
): void {
    if (logger) {
        emitExtensionEventLog(eventName, properties);
        return;
    }

    if (datadogLogsDisabled) {
        return;
    }

    if (
        bufferedExtensionEventLogs.length >= MAX_BUFFERED_EXTENSION_EVENT_LOGS
    ) {
        bufferedExtensionEventLogs.shift();
    }
    bufferedExtensionEventLogs.push({ eventName, properties });
}

function emitExtensionEventLog(
    eventName: string,
    properties: Record<string, unknown>,
): void {
    logger?.info(eventName, {
        event_name: eventName,
        count: 1,
        ...properties,
    });
}
